import React from "react";
import { C, radii, shadows, space, type } from "../../styles/tokens";

/**
 * OptionTiles (docs/BRAND_MANUAL.md, section 8)
 * Two or three ways to do one thing side by side (Aadhaar OTP vs DigiLocker; GSTIN vs PAN):
 *  - 40px icon circle (sunken #EEF2F6)
 *  - bodyStrong title (15px 600) + caption (13px 400)
 *  - Selected: outlined 1.5px primary (#08833E) on primaryTint (#E7F4EC)
 */
export function OptionTiles({ label, options = [], value, onChange }) {
  return (
    <div style={{ marginBottom: space.lg }}>
      {label && (
        <label
          style={{
            display: "block",
            marginBottom: space.sm,
            color: C.textStrong,
            fontSize: type.label.fontSize,
            lineHeight: type.label.lineHeight,
            fontWeight: type.label.fontWeight,
          }}
        >
          {label}
        </label>
      )}

      <div
        role="radiogroup"
        aria-label={label || "Selection options"}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: space.md, // 12px
        }}
      >
        {options.map(({ key, Icon, title, detail, disabled = false }) => {
          const selected = key === value;

          return (
            <button
              type="button"
              key={key}
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => !disabled && onChange(key)}
              style={{
                display: "flex",
                alignItems: "center",
                textAlign: "left",
                gap: space.md,
                padding: space.md,
                borderRadius: radii.card, // 16px
                border: selected
                  ? `1.5px solid ${C.primary}`
                  : `1.5px solid ${C.divider}`,
                backgroundColor: selected ? C.primaryTint : C.surface,
                boxShadow: shadows.card,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                transition: "border-color 0.15s ease-out, background-color 0.15s ease-out",
                width: "100%",
                boxSizing: "border-box",
                outline: "none",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: radii.pill,
                  backgroundColor: selected ? C.surface : C.surfaceSunken,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: selected ? C.primary : C.navy,
                  transition: "background-color 0.15s, color 0.15s",
                }}
              >
                {Icon && <Icon size={20} strokeWidth={2} />}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: C.textStrong,
                    fontSize: type.bodyStrong.fontSize,
                    lineHeight: type.bodyStrong.lineHeight,
                    fontWeight: type.bodyStrong.fontWeight,
                  }}
                >
                  {title}
                </div>
                {detail && (
                  <div
                    style={{
                      color: C.textMuted,
                      fontSize: type.caption.fontSize,
                      lineHeight: type.caption.lineHeight,
                      fontWeight: type.caption.fontWeight,
                      marginTop: 2,
                    }}
                  >
                    {detail}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
